import {
  YES_NO,
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  penaltyDeclaration,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import {
  ARCHITECTURE_ORGAN,
  PB,
  allOf,
  every,
  investorSection,
  localPlanField,
  otherLawsAttachment,
  propertySection,
  rightToDisposeAttachment,
  rightToDisposeSection,
  zoningDecisionAttachment,
} from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * F6. Zgłoszenie budowy lub wykonywania innych robót budowlanych (PB-2) albo budowy lub przebudowy budynku mieszkalnego
 * jednorodzinnego (PB-2a) — art. 29 i 30 ustawy Prawo budowlane (t.j. Dz. U. 2025 poz. 418, tekst
 * https://api.sejm.gov.pl/eli/acts/DU/2025/418/text.pdf — pobrany 2026-10-22). Wzory GUNB (wersja 2026): PB-2
 * https://www.gov.pl/attachment/58b0b8c6-a322-4904-a99d-54cedff665ff, PB-2a
 * https://www.gov.pl/attachment/1fff0e94-80a0-43ba-acb8-6e18d4d2ab65 — pobrane 2026-10-22 (DOCX). Załączniki z art. 30
 * ust. 2a, 4b i 4c; oświadczenia inwestora przy budowie domu do 70 m² (art. 29 ust. 1 pkt 1a) z art. 30 ust. 2a pkt 6–7 —
 * z klauzulą art. 233 § 6 KK, którą przewiduje ustawa. Oświadczenie PB-5 składane w formularzu.
 */
const PB2: EbiCondition = { field: "rodzajZgloszenia", equals: "pb2" };
const PB2A: EbiCondition = { field: "rodzajZgloszenia", equals: "pb2a" };
const CHOSEN: EbiCondition = { field: "rodzajZgloszenia", filled: true };
const DESIGN_REQUIRED: EbiCondition = { any: [PB2A, every(PB2, { field: "projektBudowlanyWymagany", equals: "tak" })] };
const SMALL_HOUSE: EbiCondition = every(PB2A, { field: "dom70", equals: "tak" });

const F6: IEbiFormDefinition = {
  code: "F6",
  version: 1,
  title: "Zgłoszenie budowy lub wykonywania robót budowlanych",
  category: EEbiFormCategory.Construction,
  office: EEbiFormOffice.County,
  documentType: "Pismo",
  description: "Zgłoszenie robót budowlanych niewymagających pozwolenia (PB-2) albo budowy lub przebudowy domu jednorodzinnego (PB-2a).",
  keywords: ["zgłoszenie", "PB-2", "PB-2a", "roboty budowlane", "dom jednorodzinny", "70 m2", "budowa bez pozwolenia"],
  organ: ARCHITECTURE_ORGAN,
  legalBasis: `art. 29 i art. 30 ${PB}`,
  request: "Zgłaszam zamiar wykonania robót budowlanych opisanych w zgłoszeniu.",
  feeLabel: "bez opłaty",
  fee: "Zgłoszenie co do zasady bez opłaty; opłata skarbowa tylko przy zgłoszeniach objętych stawkami za pozwolenie na budowę (np. sieci powyżej 1 km — 2143 zł, do 1 km — 105 zł, inne budowle — 155 zł); budownictwo mieszkaniowe zwolnione; pełnomocnictwo — 17 zł.",
  deadlineLabel: "21 dni na sprzeciw",
  deadline:
    "Organ może wnieść sprzeciw w terminie 21 dni od dnia doręczenia zgłoszenia (art. 30 ust. 5); budowę domu do 70 m² można rozpocząć po doręczeniu zgłoszenia (art. 30 ust. 5k).",
  variantField: "rodzajZgloszenia",
  variants: {
    pb2: { title: "Zgłoszenie budowy lub wykonywania innych robót budowlanych (PB-2)", documentType: "Pismo" },
    pb2a: { title: "Zgłoszenie budowy lub przebudowy budynku mieszkalnego jednorodzinnego (PB-2a)", documentType: "Pismo" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj zgłoszenia",
      fields: [
        {
          code: "rodzajZgloszenia",
          type: "radio",
          label: "Zgłaszam",
          required: true,
          options: [
            {
              value: "pb2",
              label: "budowę lub wykonywanie innych robót budowlanych (PB-2) — nie dotyczy budowy i przebudowy budynku mieszkalnego jednorodzinnego",
            },
            { value: "pb2a", label: "budowę lub przebudowę budynku mieszkalnego jednorodzinnego (PB-2a)" },
          ],
        },
      ],
    },
    applicantSection(),
    representationSection(),
    investorSection(),
    {
      code: "roboty",
      title: "Informacje o robotach budowlanych",
      visibleWhen: PB2,
      fields: [
        { code: "opisRobot", type: "textarea", label: "Rodzaj, zakres i sposób wykonywania robót budowlanych", required: true, maxLength: 5000 },
        {
          code: "projektBudowlanyWymagany",
          type: "radio",
          label:
            "Czy zgłoszenie dotyczy budowy z art. 29 ust. 1 pkt 2–4 albo przebudowy lub instalowania z art. 29 ust. 3 pkt 1 lit. a i pkt 3 lit. d Prawa budowlanego (wymagany projekt zagospodarowania działki i projekt architektoniczno-budowlany)?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
        {
          code: "projektZagospodarowaniaWymagany",
          type: "radio",
          label:
            "Czy zgłoszenie dotyczy budowy z art. 29 ust. 1 pkt 9, 23, 27, 28, 30 lub 30a albo instalowania z art. 29 ust. 3 pkt 3 lit. e (np. sieci, przydomowa oczyszczalnia ścieków, instalacja gazowa) — wymagany projekt zagospodarowania działki lub terenu?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
      ],
    },
    {
      code: "zamierzenie",
      title: "Rodzaj zamierzenia budowlanego",
      visibleWhen: PB2A,
      fields: [
        {
          code: "rodzajZamierzenia",
          type: "checkboxes",
          label: "Rodzaj zamierzenia (wskaż wszystkie właściwe)",
          required: true,
          options: [
            { value: "budowa", label: "budowa nowego budynku (nowych budynków)" },
            { value: "rozbudowa", label: "rozbudowa budynku (budynków)" },
            { value: "przebudowa", label: "przebudowa budynku (budynków)" },
            { value: "nadbudowa", label: "nadbudowa budynku (budynków)" },
            { value: "odbudowa", label: "odbudowa budynku (budynków)" },
          ],
        },
        {
          code: "dom70",
          type: "radio",
          label:
            "Czy zgłoszenie dotyczy budowy wolno stojącego, nie więcej niż dwukondygnacyjnego budynku mieszkalnego jednorodzinnego o powierzchni zabudowy do 70 m² (art. 29 ust. 1 pkt 1a Prawa budowlanego)?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
      ],
    },
    {
      code: "planMiejscowy",
      title: "Plan miejscowy",
      visibleWhen: DESIGN_REQUIRED,
      fields: [localPlanField()],
    },
    {
      code: "termin",
      title: "Planowany termin rozpoczęcia robót budowlanych",
      visibleWhen: CHOSEN,
      fields: [
        {
          code: "terminRozpoczecia",
          type: "date",
          label: "Planowany termin rozpoczęcia robót",
          required: true,
          width: "half",
          hint: "Przy budowie tymczasowego obiektu budowlanego podaj w opisie robót także planowany termin jego rozbiórki lub przeniesienia.",
        },
      ],
    },
    {
      code: "dom70Oswiadczenia",
      title: "Oświadczenia inwestora — budynek do 70 m² (art. 30 ust. 2a pkt 6 i 7 Prawa budowlanego)",
      visibleWhen: SMALL_HOUSE,
      fields: [
        {
          code: "dom70Cel",
          type: "declaration",
          label: "Oświadczam, że planowana budowa jest prowadzona w celu zaspokojenia własnych potrzeb mieszkaniowych.",
          required: true,
        },
        penaltyDeclaration("dom70Klauzula", "Jestem świadomy(-ma) odpowiedzialności karnej za złożenie fałszywego oświadczenia."),
        {
          code: "dom70Kierownik",
          type: "declaration",
          label: "Oświadczam, że przyjmuję odpowiedzialność za kierowanie budową w przypadku nieustanowienia kierownika budowy.",
          required: true,
        },
        { code: "dom70Kompletnosc", type: "declaration", label: "Oświadczam, że dokumentacja dołączona do zgłoszenia jest kompletna.", required: true },
      ],
    },
    propertySection(),
    rightToDisposeSection(CHOSEN),
    {
      code: "oplata",
      title: "Opłata skarbowa",
      visibleWhen: CHOSEN,
      fields: [
        feeInfo(
          "infoOplata",
          "Zgłoszenie co do zasady nie podlega opłacie skarbowej. Opłatę wnosi się tylko przy zgłoszeniach, do których stosuje się stawki za pozwolenie na budowę (np. sieci, inne budowle); sprawy budownictwa mieszkaniowego są zwolnione.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    { code: "szkice", label: "Szkice lub rysunki", hint: "W zależności od potrzeb (art. 30 ust. 2a pkt 2).", multiple: true, visibleWhen: PB2 },
    {
      code: "projektZagospodarowania",
      label: "Projekt zagospodarowania działki lub terenu (z opisem technicznym instalacji, jeśli jest wymagany)",
      hint: "Wykonany przez projektanta posiadającego uprawnienia budowlane (art. 30 ust. 2a pkt 4 i 5).",
      visibleWhen: allOf(PB2, { field: "projektZagospodarowaniaWymagany", equals: "tak" }),
      required: true,
    },
    {
      code: "projekt",
      label: "Projekt zagospodarowania działki lub terenu oraz projekt architektoniczno-budowlany",
      hint: "Art. 30 ust. 4b i 4c w zw. z art. 33 ust. 2 — w postaci elektronicznej, pliki podpisane przez projektanta.",
      multiple: true,
      visibleWhen: DESIGN_REQUIRED,
      required: true,
    },
    zoningDecisionAttachment(DESIGN_REQUIRED),
    otherLawsAttachment(
      "Np. decyzja o środowiskowych uwarunkowaniach, pozwolenie konserwatorskie, pozwolenie wodnoprawne, uzgodnienie Natura 2000 (art. 30 ust. 2a pkt 3).",
      CHOSEN,
    ),
    rightToDisposeAttachment(CHOSEN),
    ...proxyAttachments(),
    feeProofAttachment("Potwierdzenie uiszczenia opłaty skarbowej", {
      required: false,
      visibleWhen: CHOSEN,
      hint: "Tylko gdy zgłoszenie podlega opłacie skarbowej (stawki jak za pozwolenie na budowę).",
    }),
    otherAttachments(),
  ],
};

export default F6;
